import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
	getSessionStatus,
	keepSessionAlive,
} from '@dealstack/frontend/shared/dataExchange/services/session.service';
import { logoutApi } from '@dealstack/frontend/shared/dataExchange/services/auth.service';
import {
	beginIntentionalSignOut,
	isSigningOutIntentionally,
} from '@dealstack/frontend/shared/mechanism/session/intentionalSignOut';
import {
	SessionDeadline,
	anchorSessionDeadline,
	getSessionDeadline,
	setSessionDeadline,
	subscribeToSessionDeadline,
} from '@dealstack/frontend/shared/mechanism/session/sessionDeadline';
import {
	getMsSinceLastInteraction,
	startTrackingSessionActivity,
} from '@dealstack/frontend/shared/mechanism/session/sessionActivity';
import { currentReturnTarget, redirectToLoginPage } from '@dealstack/frontend/shared/redirecting';
import {
	SessionEndReason,
	SessionStatusPayload,
} from '@dealstack/shared/lib/common/session/sessionContract';
import {
	getSessionEndReason,
	isUnauthorizedApiError,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { isReportableApiFailure } from '@dealstack/frontend/shared/dataExchange/ApiLayer/reportableFailure';
import { SessionTimeoutState } from '@dealstack/frontend/shared/components/SessionTimeout/useSessionTimeout.types';
import {
	SESSION_TICK_MS,
	hasExpired,
	isWithinWarningWindow,
	secondsUntilExpiry,
	shouldSendKeepAlive,
} from '@dealstack/frontend/shared/components/SessionTimeout/sessionTimeout.utils';

/** Least often this tab will re-ask the server before acting on its own countdown. */
export const MIN_REVERIFY_INTERVAL_MS = 30 * 1000;

/** Long enough for the winner of a refresh-token rotation race to land its cookies. */
export const UNEXPLAINED_401_RECHECK_DELAY_MS = 2 * 1000;

/**
 * The deadline always comes from the server (GET /auth/session, then the headers on every
 * response), so what the user sees matches what is enforced rather than a second timer
 * that could drift out of step.
 */
export const useSessionTimeout = ({ enabled }: { enabled: boolean }): SessionTimeoutState => {
	const [deadline, setDeadline] = useState<SessionDeadline | null>(getSessionDeadline);
	const [warningSeconds, setWarningSeconds] = useState<number | null>(null);
	const [idleTimeoutSeconds, setIdleTimeoutSeconds] = useState<number | null>(null);
	const [secondsRemaining, setSecondsRemaining] = useState(0);
	const [isWarning, setIsWarning] = useState(false);
	const [endedReason, setEndedReason] = useState<SessionEndReason | null>(null);
	const [isExtending, setIsExtending] = useState(false);

	const lastKeepAliveAtRef = useRef(0);
	// Expiry can be reached from the ticker and from a rejected request at once.
	const hasEndedRef = useRef(false);
	const reverifyInFlightRef = useRef(false);
	const lastReverifyAtRef = useRef(0);
	const recheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	/**
	 * `revokeServerSide` is only for the user actually asking to sign out. Everything else
	 * has already been confirmed against the server before getting here, so there is
	 * nothing left to tell it.
	 */
	const endSession = useCallback(
		(reason: SessionEndReason, { revokeServerSide = false } = {}) => {
			// Clicking "Log out" counts as interaction, so the ticker fires a keep-alive at
			// the same moment. Logout revokes the session, that keep-alive 401s as ENDED, and
			// evicting on it would redirect to ?sessionEnd=ended — aborting the in-flight
			// logout and telling the user their session died when they asked it to.
			// `revokeServerSide` is the deliberate sign-out itself, which must still proceed.
			if (!revokeServerSide && isSigningOutIntentionally()) {
				return;
			}
			if (hasEndedRef.current) {
				return;
			}
			hasEndedRef.current = true;
			setEndedReason(reason);
			setIsWarning(false);
			setSessionDeadline(null);

			const redirect = () => redirectToLoginPage(currentReturnTarget(), reason);

			if (!revokeServerSide) {
				redirect();
				return;
			}

			// Best-effort: a failing logout must not block the redirect.
			beginIntentionalSignOut();
			logoutApi()
				.catch(() => undefined)
				.finally(redirect);
		},
		[]
	);

	const applyStatus = useCallback((status: SessionStatusPayload) => {
		setWarningSeconds(status.idleWarningSeconds);
		setIdleTimeoutSeconds(status.idleTimeoutSeconds);
		setSessionDeadline(anchorSessionDeadline(status));
	}, []);

	/**
	 * A 401 the server gave no reason for is a failed token refresh, not an expiry: a real
	 * idle or absolute expiry always says so. WorkOS answers the loser of a refresh-token
	 * rotation race (two tabs refreshing at once) the same way, and that session is still
	 * alive, so ask once more before ending it, and never call it inactivity.
	 */
	const endOrRecheck = useCallback(
		(error: unknown) => {
			const reason = getSessionEndReason(error);
			if (reason) {
				endSession(reason);
				return;
			}
			if (recheckTimeoutRef.current !== null) {
				return;
			}

			recheckTimeoutRef.current = setTimeout(() => {
				getSessionStatus()
					.then(applyStatus)
					.catch((recheckError) => {
						if (isUnauthorizedApiError(recheckError)) {
							endSession(getSessionEndReason(recheckError) ?? SessionEndReason.ENDED);
							return;
						}
						if (isReportableApiFailure(recheckError)) {
							Sentry.captureException(recheckError);
						}
					})
					.finally(() => {
						recheckTimeoutRef.current = null;
					});
			}, UNEXPLAINED_401_RECHECK_DELAY_MS);
		},
		[applyStatus, endSession]
	);

	useEffect(
		() => () => {
			if (recheckTimeoutRef.current !== null) {
				clearTimeout(recheckTimeoutRef.current);
			}
		},
		[]
	);

	/**
	 * Asks the server before believing this tab's own countdown. A tab that was suspended,
	 * throttled, or simply never saw the response that moved the deadline would otherwise
	 * evict a session that is still perfectly alive. The server is the only thing that
	 * knows, so ask it: a 401 ends the session for the reason it gives, and anything else
	 * refreshes the deadline and carries on.
	 */
	const reverify = useCallback(() => {
		const now = Date.now();

		if (
			reverifyInFlightRef.current ||
			now - lastReverifyAtRef.current < MIN_REVERIFY_INTERVAL_MS
		) {
			return;
		}
		reverifyInFlightRef.current = true;
		lastReverifyAtRef.current = now;

		getSessionStatus()
			.then(applyStatus)
			.catch((error) => {
				if (isUnauthorizedApiError(error)) {
					endOrRecheck(error);
					return;
				}
				// Offline or a failing API is not proof the session is over. Leave it
				// alone; the server rejects the next real request if it disagrees.
				if (isReportableApiFailure(error)) {
					Sentry.captureException(error);
				}
			})
			.finally(() => {
				reverifyInFlightRef.current = false;
			});
	}, [applyStatus, endOrRecheck]);

	useEffect(() => startTrackingSessionActivity(), []);

	useEffect(() => subscribeToSessionDeadline(setDeadline), []);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		let cancelled = false;

		getSessionStatus()
			.then((status) => {
				if (cancelled) {
					return;
				}
				applyStatus(status);
			})
			.catch((error) => {
				if (cancelled) {
					return;
				}
				if (isUnauthorizedApiError(error)) {
					endOrRecheck(error);
					return;
				}
				// Without the policy there is nothing to count down to. The server still
				// enforces the timeout, so the worst case is losing the warning.
				if (isReportableApiFailure(error)) {
					Sentry.captureException(error);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [enabled, endOrRecheck, applyStatus]);

	const extendSession = useCallback(() => {
		setIsExtending(true);
		lastKeepAliveAtRef.current = Date.now();

		keepSessionAlive()
			.then(applyStatus)
			.catch((error) => {
				if (isUnauthorizedApiError(error)) {
					endOrRecheck(error);
					return;
				}
				if (isReportableApiFailure(error)) {
					Sentry.captureException(error);
				}
			})
			.finally(() => setIsExtending(false));
	}, [applyStatus, endOrRecheck]);

	const signOutNow = useCallback(
		() => endSession(SessionEndReason.SIGNED_OUT, { revokeServerSide: true }),
		[endSession]
	);

	useEffect(() => {
		if (!enabled || !deadline || warningSeconds === null || idleTimeoutSeconds === null) {
			return;
		}

		const tick = () => {
			const now = Date.now();

			if (hasExpired(deadline, now)) {
				// Confirm with the server rather than evicting on this tab's copy. The
				// dialog stays up showing 0:00 until it answers.
				setSecondsRemaining(0);
				reverify();
				return;
			}

			const withinWarning = isWithinWarningWindow(deadline, now, warningSeconds);

			if (withinWarning) {
				setSecondsRemaining(secondsUntilExpiry(deadline, now));
				// Same reason as above: do not show a countdown this tab may have missed
				// the cancellation of. Throttled, so an open dialog costs little.
				reverify();
			}
			setIsWarning(withinWarning);

			if (
				shouldSendKeepAlive({
					deadline,
					now,
					lastKeepAliveAt: lastKeepAliveAtRef.current,
					idleTimeoutSeconds,
					warningSeconds,
					msSinceLastInteraction: getMsSinceLastInteraction(),
				})
			) {
				extendSession();
			}
		};

		tick();
		const intervalId = setInterval(tick, SESSION_TICK_MS);

		return () => clearInterval(intervalId);
	}, [enabled, deadline, warningSeconds, idleTimeoutSeconds, extendSession, reverify]);

	return { isWarning, secondsRemaining, endedReason, isExtending, extendSession, signOutNow };
};
